import { useEffect, useMemo, useRef, useState } from "react";

import {
  PalButton,
  PalCodeField,
  PalIconButton,
  PalMenu,
  PalMenuItem,
  PalPagination,
  PalSwitch,
  PalTextField,
  PalTooltip,
  PalTypography,
} from "@palamar/fe-library";
import { withCardon } from "cardon";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import styled from "@emotion/styled";
import {
  Add,
  Delete,
  DescriptionOutlined,
  Done,
  Edit,
  ErrorOutline,
  Language,
  MoreVert,
  TextFields,
} from "@mui/icons-material";
import { Alert, Box, CircularProgress, Divider, Grid, ListItemIcon, ListItemText } from "@mui/material";

import PRDropZone from "~components/Generic/PRDropZone";
import PRModal from "~components/Generic/PRModal";
import {
  dialogComponentsRagFactGroupStatus,
  onboardingRagStatus,
  onboardingRagStatusMap,
  onboardingRagType,
} from "~constants";
import AlertHelper from "~helpers/AlertHelper";
import DateHelper from "~helpers/DateHelper";
import DialogHelper from "~helpers/DialogHelper";
import {
  getRagFactGroup,
  postMessageToRag,
  processRag,
  stopRag,
} from "~store/dialogComponents/scenarioManager/actions";
import {
  createOrUpdateSourceFile,
  createOrUpdateSourceWebPage,
  deleteSourceFile,
  deleteSourceWebPage,
  getSourceFile,
  getSourceWebPage,
} from "~store/onboarding/actions";
import { selectProject } from "~store/settings/projectSettings/selectors";

import { IFRAME_PREVIEW_ID, StepContainer } from "../CommonComponents";

const KnowledgeBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 2px solid ${(props) => (props.selected ? props.theme.palette.primary.main : props.theme.palette.grey[300])};
  border-radius: 4px;
  padding: ${(props) => props.theme.spacing(4)};
  cursor: pointer;
  ${(props) => props.selected && `background-color: #e8f6fc;`}
`;

const AddKnowledgeSourceModal = withCardon(
  function AddKnowledgeSourceModalContent({ get }) {
    const formik = useFormik({
      initialValues: {
        type: onboardingRagType.webpage,
        url: "",
        files: null,
        content: "",
      },
      onSubmit: async (values) => {
        get({
          ...values,
        })();
      },
    });

    const handleSelectBox = (type) => () => {
      formik.setFieldValue("type", type);
    };

    const handleChangeUrl = (e) => {
      formik.setFieldValue("url", e.target.value);
    };
    const handleChangeFile = (files) => {
      formik.setFieldValue("files", files);
    };

    // const handleChangeContent = (e) => {
    //   formik.setFieldValue("content", e.target.value);
    // };

    return (
      <PRModal size="xl" title={"Add Knowledge Source"} onClick={formik.handleSubmit} onClose={get(false)}>
        {/* <PalTextField fullWidth label="Enter Email" value={email} onChange={handleChange} /> */}

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <KnowledgeBox
              selected={formik.values.type === onboardingRagType.webpage}
              onClick={handleSelectBox(onboardingRagType.webpage)}
            >
              <Language />
              <PalTypography fontWeight={600} variant="body1">
                Webpage
              </PalTypography>
            </KnowledgeBox>
          </Grid>
          <Grid item xs={4}>
            <KnowledgeBox
              selected={formik.values.type === onboardingRagType.file}
              onClick={handleSelectBox(onboardingRagType.file)}
            >
              <DescriptionOutlined />
              <PalTypography fontWeight={600} variant="body1">
                File
              </PalTypography>
            </KnowledgeBox>
          </Grid>
          <Grid item xs={4}>
            <KnowledgeBox
              selected={formik.values.type === onboardingRagType.content}
              onClick={handleSelectBox(onboardingRagType.content)}
            >
              <TextFields />
              <PalTypography fontWeight={600} variant="body1">
                Text
              </PalTypography>
            </KnowledgeBox>
          </Grid>
          {formik.values.type === onboardingRagType.webpage && (
            <Grid item xs={12}>
              <PalTypography variant="body2">Public URL</PalTypography>
              <PalTextField fullWidth placeholder="Enter URL" value={formik.values.url} onChange={handleChangeUrl} />
            </Grid>
          )}

          {formik.values.type === onboardingRagType.file && (
            <Grid item xs={12}>
              <PRDropZone
                // accept={[".zip"]}
                multiple
                maxFiles={10}
                onFileChange={handleChangeFile}
              />
            </Grid>
          )}
          {formik.values.type === onboardingRagType.content && (
            <Grid item xs={12}>
              <PalCodeField
                editorMode
                fullWidth
                editorProps={{
                  defaultHeight: 400,
                }}
                name="content"
                value={formik.values.content}
                onChange={formik.handleChange}
              />
            </Grid>
          )}
        </Grid>
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

const SourceBox = styled.div`
  border-radius: 4px;
  padding: ${(props) => props.theme.spacing(2)};
  /* cursor: pointer; */
  border: 1px solid ${(props) => props.theme.palette.grey[300]};
`;

function StepRagItem({ item, projectId, formik, onModified, stepKey, setFieldValue }) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const { t } = useTranslation();

  const onClickOptions = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const onClose = () => {
    setAnchorEl(null);
  };

  const handleClickEditRagContent = async () => {
    setAnchorEl(null);
    if (item.type === onboardingRagType.file) {
      const newPages = await EditRagFilePagesModal.show({ item: item.payload });
      if (newPages) {
        const newItem = { ...item };
        newItem.payload.source_pages = newPages;
        await dispatch(createOrUpdateSourceFile(projectId, newItem.payload));
        setFieldValue(
          "sourceList",
          formik.values[stepKey].sourceList.map((source) => (source.payload.id === item.payload.id ? newItem : source))
        );
        onModified?.();
      }
    } else {
      const content = item.type === onboardingRagType.content ? item.payload.text : item.payload.content;
      const newContent = await EditRagContentModal.show({ content, item: item.payload });
      if (newContent) {
        const newItem = { ...item };
        if (item.type === onboardingRagType.content) {
          newItem.payload.text = newContent;
        } else if (item.type === onboardingRagType.webpage) {
          newItem.payload.content = newContent;
        }
        setFieldValue(
          "sourceList",
          formik.values[stepKey].sourceList.map((source) => (source.payload.id === item.payload.id ? newItem : source))
        );

        onModified?.();
      }
    }
  };

  const handleClickDeleteRagItem = async () => {
    setAnchorEl(null);
    if (!(await DialogHelper.showQuestionDelete())) {
      return;
    }
    if (item.type === onboardingRagType.webpage) {
      await dispatch(deleteSourceWebPage(projectId, item.payload.id));
    } else if (item.type === onboardingRagType.file) {
      await dispatch(deleteSourceFile(projectId, item.payload.id));
    }
    setFieldValue(
      "sourceList",
      formik.values[stepKey].sourceList.filter((source) => source.payload.id !== item.payload.id)
    );
  };

  const isProcessingMain = [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(item.payload.status);

  const isProcessingFilePages =
    item.type === onboardingRagType.file &&
    item.payload.source_pages.some((page) =>
      [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(page.status)
    );

  const isProcessing = isProcessingMain || isProcessingFilePages;
  const isError = item.payload.status === onboardingRagStatus.Error;

  const processedPageCount =
    item.type === onboardingRagType.file &&
    !!item.payload.source_pages &&
    item.payload.source_pages.filter(
      (i) => ![onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(i.status)
    );

  const processingProgress =
    item.type === onboardingRagType.file && !!item.payload.source_pages?.length
      ? ` (${processedPageCount?.length}/${item.payload.source_pages?.length})`
      : "";

  return (
    // <Grid key={index} item xs={12}>
    <SourceBox>
      <Grid container alignItems="center" spacing={2} wrap="nowrap">
        <Grid item xs={"auto"}>
          <PalTooltip
            title={`${
              item.type === onboardingRagType.webpage
                ? "Webpage"
                : item.type === onboardingRagType.file
                  ? "File"
                  : "Content"
            }`}
          >
            <span>
              {item.type === onboardingRagType.webpage && <Language />}
              {item.type === onboardingRagType.file && <DescriptionOutlined />}
              {item.type === onboardingRagType.content && <TextFields />}
            </span>
          </PalTooltip>
        </Grid>
        <Grid item xs overflow={"hidden"} textOverflow={"ellipsis"}>
          {/* Status : {onboardingRagStatusMap[item.payload.status] || item.payload.status} */}

          {item.type === onboardingRagType.webpage && (
            <PalTypography noWrap href={item.payload.url} variant="body1">
              <PalTooltip title={item.payload.url}>
                <span>{item.payload.url}</span>
              </PalTooltip>
            </PalTypography>
          )}
          {item.type === onboardingRagType.file && (
            <PalTypography noWrap variant="body1">
              <PalTooltip title={`${item.payload.media_file.split("/").pop()}${processingProgress}`}>
                <span>
                  {item.payload.media_file.split("/").pop()}
                  <PalTypography variant="caption">{processingProgress}</PalTypography>
                </span>
              </PalTooltip>
            </PalTypography>
          )}
          {item.type === onboardingRagType.content && (
            <PalTypography noWrap variant="body1">
              {item.payload.text}
              <PalTooltip
                title={item.payload.text?.length > 250 ? `${item.payload.text.slice(0, 250)}...` : item.payload.text}
              >
                <span></span>
              </PalTooltip>
            </PalTypography>
          )}
        </Grid>

        <Grid item alignItems="center" display="flex" justifyContent="center" xs="auto">
          <PalTooltip title={t(onboardingRagStatusMap[item.payload.status]) || item.payload.status}>
            <span>
              {isError ? (
                <ErrorOutline color="error" />
              ) : isProcessing ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <Done color="success" />
              )}
            </span>
          </PalTooltip>
        </Grid>
        <Grid item alignItems="center" display="flex" justifyContent="center" xs="auto">
          <PalIconButton onClick={onClickOptions}>
            <MoreVert />
          </PalIconButton>
          <PalMenu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onClose}>
            <PalMenuItem disabled={isProcessing} onClick={handleClickEditRagContent}>
              <ListItemIcon>
                <Edit fontSize="small" />
              </ListItemIcon>
              <ListItemText> {t("dashboard.onboarding.updateRagContent")}</ListItemText>
            </PalMenuItem>
            <Divider />
            <PalMenuItem onClick={handleClickDeleteRagItem}>
              <ListItemIcon>
                <Delete color="error" fontSize="small" />
              </ListItemIcon>
              <ListItemText color="error"> {t("dashboard.onboarding.deleteRag")}</ListItemText>
            </PalMenuItem>
          </PalMenu>
        </Grid>
      </Grid>
    </SourceBox>
    // </Grid>
  );
}

const EditRagContentModal = withCardon(
  function EditRagContentModalContent({ get, content, item }) {
    const formik = useFormik({
      initialValues: {
        content,
      },
      onSubmit: async (values) => {
        get(values.content)();
      },
    });

    const handleChangeContent = (e) => {
      formik.setFieldValue("content", e.target.value);
    };
    const totalContentWordCount = formik.values.content.split(/\s+/).filter(Boolean)?.length;

    return (
      <PRModal size="xl" title={"Edit Content"} onClick={formik.handleSubmit} onClose={get(false)}>
        {item.url && (
          <PalTypography mb={1}>
            Source: <strong>{item.url}</strong>
          </PalTypography>
        )}
        <PalTypography mb={1}>
          Total word count: <strong>{totalContentWordCount}</strong>
        </PalTypography>
        <PalCodeField
          editorMode
          fullWidth
          editorProps={{
            defaultHeight: 800,
          }}
          name="content"
          value={formik.values.content}
          onChange={handleChangeContent}
        />
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

const EditRagFilePagesModal = withCardon(
  function EditRagFilePagesModalContent({ get, item }) {
    const [currentPage, setCurrentPage] = useState(1);
    const [showBlankPages, setShowBlankPages] = useState(false);

    const formik = useFormik({
      initialValues: {
        sourcePages: item.source_pages,
      },
      onSubmit: async (values) => {
        get(values.sourcePages)();
      },
    });

    const visiblePages = useMemo(() => {
      if (showBlankPages) {
        return formik.values.sourcePages;
      }
      const visiblePages = formik.values.sourcePages.filter((page) => page.content.trim()?.length > 0);
      return visiblePages;
    }, [formik.values.sourcePages, showBlankPages]);

    const handleChangeContent = (item) => (e) => {
      const newItem = { ...item };
      newItem.content = e.target.value;
      const newSourcePages = formik.values.sourcePages.map((i) => (i.id === item.id ? newItem : i));
      formik.setFieldValue("sourcePages", newSourcePages);
    };

    const totalContentWordCount = visiblePages.reduce(
      (acc, item) => acc + item.content.split(/\s+/).filter(Boolean)?.length,
      0
    );

    const handleToggleShowBlankPages = () => {
      setShowBlankPages((prev) => !prev);
    };

    return (
      <PRModal
        size="xl"
        title={`Edit Content - ${currentPage}/${visiblePages?.length}`}
        onClick={formik.handleSubmit}
        onClose={get(false)}
      >
        <PalTypography alignItems={"center"} display={"flex"} gap={0.5} mb={1}>
          Show Blank pages
          <PalSwitch checked={showBlankPages} onChange={handleToggleShowBlankPages} />
        </PalTypography>
        <PalTypography mb={1}>
          Source file: <strong>{item.media_file?.split("/").pop()}</strong>
        </PalTypography>
        <PalTypography mb={1}>
          Total word count: <strong>{totalContentWordCount}</strong>
        </PalTypography>
        <Box position={"relative"}>
          <PalCodeField
            editorMode
            fullWidth
            disabled={!visiblePages?.length}
            editorProps={{
              defaultHeight: 800,
            }}
            name="content"
            value={visiblePages[currentPage - 1]?.content}
            onChange={handleChangeContent(visiblePages[currentPage - 1])}
          />
          {!visiblePages.length && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                backgroundColor: "rgb(224 224 224 / 30%)",
                padding: 1,
                borderRadius: 0.5,
                textAlign: "center",
              }}
            >
              <PalTypography variant="h6">No content found in this file</PalTypography>
              {/* {!showBlankPages && (
                <PalTypography color={"textSecondary"} variant="body1">
                  Please enable "Show Blank pages" to view all pages
                </PalTypography>
              )} */}
            </Box>
          )}
        </Box>
        <Box alignItems="center" display="flex" justifyContent="center" mt={1}>
          {!!visiblePages?.length && (
            <PalPagination
              count={visiblePages?.length}
              page={currentPage}
              onChange={(e, page) => setCurrentPage(page)}
            />
          )}
        </Box>
      </PRModal>
    );
  },
  { destroyOnHide: true }
);

const AlertButton = styled(PalButton)`
  padding: 0 ${(props) => props.theme.spacing(0.5)};
  margin: 0 ${(props) => props.theme.spacing(0.5)};
`;
const knowledgeSourceMaxCount = 10;
function StepRag({ formik, nextAction, stepKey, setFieldValue }) {
  const project = useSelector(selectProject);
  const projectId = project.id;
  const [ragFactGroup, setRagFactGroup] = useState(formik.values[stepKey].ragFactGroup);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const ragMessageListRef = useRef([]);

  const { popupPalActions, popupInternal } = useMemo(() => {
    const iframeDom = document.getElementById(IFRAME_PREVIEW_ID);
    const palActions = iframeDom?.contentWindow?.palActions;
    const internalDoc = iframeDom?.contentWindow?.document;

    const innerIframe = internalDoc?.getElementById("_palamar_chat_iframe");
    const innerWindow = innerIframe?.contentWindow;
    const popup = innerWindow?.popup;

    ragMessageListRef.current = [
      {
        type: "notification",
        position: "center",
        messageTime: DateHelper.getDateTime().add(-1, "minute").toISOString(),
        text: "Use the chatbot to interact with the RAG model.",
      },
    ];
    palActions?.setMinimize(false);
    if (popup) {
      popup.setMessageList(ragMessageListRef.current);
      popup.setPreviewState({ enableInput: true });

      popup.onMessage = async (message) => {
        ragMessageListRef.current.push({
          type: "text",
          position: "right",
          text: message.text,
          messageTime: DateHelper.getDateTime().toISOString(),
          sender_type: "CUS",
          isSent: true,
          format: "markdown",
        });

        popup.setMessageList(ragMessageListRef.current);
        popup.setPreviewState({ typing: true });

        let response;

        try {
          response = await dispatch(postMessageToRag(projectId, formik.values[stepKey].ragFactGroupId, message.text));
        } catch {}

        popup.setPreviewState({ typing: false });
        // messages.push({
        //   type: "text",
        //   position: "left",
        //   sender_type: "BOT",
        //   messageTime: DateHelper.getDateTime(),
        //   text: response?.output,
        //   isSent: true,
        //   format: "markdown",
        // });
        if (response) {
          ragMessageListRef.current.push({
            type: "text",
            position: "left",
            text: response?.output,
            messageTime: DateHelper.getDateTime().toISOString(),
            sender_type: "BOT",
            isSent: true,
            format: "markdown",
          });

          popup.setMessageList(ragMessageListRef.current);
        }
      };
    }
    return { popupPalActions: palActions, popupInternal: popup };
  }, [dispatch, projectId, formik.values[stepKey].ragFactGroupId]);

  const handleAddKnowledgeSource = async () => {
    const data = await AddKnowledgeSourceModal.show();
    if (data.type === onboardingRagType.webpage) {
      const response = await dispatch(createOrUpdateSourceWebPage(projectId, { url: data.url }));
      setFieldValue("sourceList", [
        ...formik.values[stepKey].sourceList,
        { type: onboardingRagType.webpage, payload: response },
      ]);
      setRagFactGroup((prev) => ({ ...prev, status: dialogComponentsRagFactGroupStatus.Modified }));
    } else if (data.type === onboardingRagType.file) {
      for (const file of data.files) {
        const response = await dispatch(createOrUpdateSourceFile(projectId, { file }));
        setFieldValue("sourceList", [
          ...formik.values[stepKey].sourceList,
          { type: onboardingRagType.file, payload: response },
        ]);
      }
      setRagFactGroup((prev) => ({ ...prev, status: dialogComponentsRagFactGroupStatus.Modified }));
    } else if (data.type === onboardingRagType.content) {
      const allContentItems = formik.values[stepKey].sourceList.filter(
        (item) => item.type === onboardingRagType.content
      );
      const highestId = allContentItems.length > 0 ? Math.max(...allContentItems.map((item) => item.id)) : 0;
      setFieldValue("sourceList", [
        ...formik.values[stepKey].sourceList,
        {
          type: onboardingRagType.content,
          payload: {
            id: highestId + 1,
            text: data.content,
            status: onboardingRagStatus.Processed,
          },
        },
      ]);
      setRagFactGroup((prev) => ({ ...prev, status: dialogComponentsRagFactGroupStatus.Modified }));
    }
  };
  useEffect(() => {
    const processingSources = formik.values[stepKey].sourceList.filter(
      (item) =>
        [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(item.payload.status) ||
        (item.type === onboardingRagType.file &&
          item.payload.source_pages.some((page) =>
            [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(page.status)
          ))
    );
    if (processingSources.length > 0) {
      const interval = setInterval(() => {
        processingSources.forEach(async (item, index) => {
          const response =
            item.type === onboardingRagType.webpage
              ? await dispatch(getSourceWebPage(projectId, item.payload.id))
              : await dispatch(getSourceFile(projectId, item.payload.id));
          const isProcessing = [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(response.status);
          const isPagesProcessing =
            item.type === onboardingRagType.file
              ? response.source_pages.some((page) =>
                  [onboardingRagStatus.Processing, onboardingRagStatus.Fresh].includes(page.status)
                )
              : false;

          if (!isProcessing || isPagesProcessing) {
            const currentSourceList = [...formik.values[stepKey].sourceList];
            const itemIndex = currentSourceList.findIndex((item) => item.payload.id === response.id);
            if (itemIndex !== -1) {
              currentSourceList[itemIndex].payload = response;
            } else {
              currentSourceList[index].payload = response;
            }
            setFieldValue("sourceList", currentSourceList);
          }
        });
      }, 5000);
      return () => clearInterval(interval);
    }

    return () => {};
  }, [projectId, dispatch, formik.values[stepKey].sourceList]);

  useEffect(() => {
    const enableInput =
      [dialogComponentsRagFactGroupStatus.Modified, dialogComponentsRagFactGroupStatus.Processed].includes(
        ragFactGroup?.status
      ) && formik.values[stepKey]?.sourceList?.length > 0;

    popupInternal?.setPreviewState?.({ enableInput });
  }, [ragFactGroup?.status, popupInternal, formik.values[stepKey]?.sourceList, stepKey]);

  // useEffect(() => {
  //   if (ragFactGroup?.status === dialogComponentsRagFactGroupStatus.Processing) {
  //     const interval = setInterval(async () => {
  //     // const ragFactGroup = dispatch(getRagFactGroup(projectId, formik.values[stepKey].ragIntentId));
  //   }
  // }, [ragFactGroup]);

  useEffect(() => {
    if (ragFactGroup && ragFactGroup?.status === dialogComponentsRagFactGroupStatus.Processing) {
      const interval = setInterval(async () => {
        const response = await dispatch(getRagFactGroup(projectId, ragFactGroup.id));
        if (response?.status === dialogComponentsRagFactGroupStatus.Processed) {
          clearInterval(interval);
          AlertHelper.showSuccess(t("component.rag.ragProcessedAlert"));
          setRagFactGroup(response);
        } else if (response?.status === dialogComponentsRagFactGroupStatus.Error) {
          clearInterval(interval);
          AlertHelper.showWarning(t("component.rag.ragErrorAlert"));
          setRagFactGroup(response);
        }
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [ragFactGroup, dispatch, projectId, t]);

  const handleTrain = async (e) => {
    await nextAction(e, { preventContinue: true });
    await dispatch(processRag(projectId, formik.values[stepKey].ragFactGroupId));
    const ragFactGroup = await dispatch(getRagFactGroup(projectId, formik.values[stepKey].ragFactGroupId));
    setRagFactGroup(ragFactGroup);
  };

  const handleStopTrain = async () => {
    await dispatch(stopRag(projectId, formik.values[stepKey].ragFactGroupId));
    const ragFactGroup = await dispatch(getRagFactGroup(projectId, formik.values[stepKey].ragFactGroupId));
    setRagFactGroup(ragFactGroup);
  };

  const handleRagModified = () => {
    setRagFactGroup((prev) => ({ ...prev, status: dialogComponentsRagFactGroupStatus.Modified }));
  };
  return (
    <StepContainer>
      <Grid container spacing={2}>
        <Grid item mb={2} xs={12}>
          <PalTypography color="textPrimary" fontWeight={600} variant="h5">
            Chatbot: KnowledgeBase
          </PalTypography>
          <PalTypography gutterBottom variant="body1">
            Please provide information about your chatbot's Retrieval-Augmented Generation (RAG) capabilities.
          </PalTypography>
        </Grid>
        <Grid item xs={12}>
          {[dialogComponentsRagFactGroupStatus.Processed].includes(ragFactGroup?.status) && (
            <Alert severity="success">
              RAG model has been trained successfully. You can now interact with the chatbot to test the RAG model.
            </Alert>
          )}
          {[dialogComponentsRagFactGroupStatus.Modified, dialogComponentsRagFactGroupStatus.Fresh].includes(
            ragFactGroup?.status
          ) &&
            !!formik.values[stepKey]?.sourceList?.length && (
              <Alert severity="warning">
                RAG model has been modified. Please train the model to apply the changes.
                <AlertButton color="primary" size="medium" variant="text" onClick={handleTrain}>
                  Train
                </AlertButton>
              </Alert>
            )}
          {[dialogComponentsRagFactGroupStatus.Error].includes(ragFactGroup?.status) && (
            <Alert severity="warning">
              RAG model has an error. Please train the model again to enable the chatbot to use the RAG model.
              <AlertButton color="primary" size="medium" variant="text" onClick={handleTrain}>
                Train
              </AlertButton>
            </Alert>
          )}
          {[dialogComponentsRagFactGroupStatus.Processing].includes(ragFactGroup?.status) && (
            <Alert severity="warning">
              <Box alignItems="center" display="flex">
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Training the RAG model. Please wait...
                <AlertButton color="primary" size="medium" variant="text" onClick={handleStopTrain}>
                  Stop Train
                </AlertButton>
              </Box>
            </Alert>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box mb={1}>
            <PalTypography gutterBottom fontWeight={600} variant="body1">
              Knowledge Sources ({formik.values[stepKey]?.sourceList?.length}/{knowledgeSourceMaxCount})
            </PalTypography>
          </Box>

          <PalButton
            fullWidth
            color="primary"
            disabled={formik.values[stepKey]?.sourceList?.length >= knowledgeSourceMaxCount}
            size="medium"
            startIcon={<Add />}
            variant="contained"
            onClick={handleAddKnowledgeSource}
          >
            Add Knowledge Source
          </PalButton>
        </Grid>

        {formik.values[stepKey].sourceList.map((item, index) => {
          return (
            <Grid key={index} item xs={12}>
              <StepRagItem
                formik={formik}
                item={item}
                projectId={projectId}
                setFieldValue={setFieldValue}
                stepKey={stepKey}
                onModified={handleRagModified}
              />
            </Grid>
          );
        })}
      </Grid>
    </StepContainer>
  );
}

export default StepRag;
